import { useState, useEffect, useContext } from "react";
import { DataGrid } from "@mui/x-data-grid";
import AddItem from "../components/raidsession/AddItem";
import AddCharacter from "../components/raidsession/AddCharacter";
import AttendanceDKPUpdate from "../components/raidsession/AttendanceDKPUpdate";
import StopSession from "../components/raidsession/StopSession";
import { useParams } from "react-router-dom";
import AppContext from "../contexts/AppContext";
import { Alert, Button } from "@mui/material";
import AddonInit from "../components/addonInit/AddonInit";
import { Box, Grid, Stack } from "@mui/material";
import NavBar from "../components/appbar/NavBar";
import DeleteButton from '../components/button/DeleteButton'
import apiFetch from "../helperFunctions/api";
import useAsyncAction from "../helperFunctions/useAsyncAction";


export default function RaidSession(props) {
  let { API, characters, getCharacters, setSession, session } =
    useContext(AppContext);
  const [sessionData, setSessionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const { pending, error, run } = useAsyncAction();

  let { id } = useParams();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError("");
    apiFetch(`${API}/session/${id}`).then((response) => response.json())
      .then((data) => {
        if (active) {
          setSession(data.sessionName);
          setSessionData(data.sessionLedger);
        }
      })
      .catch((err) => {
        console.error("Unable to load raid session:", err);
        if (active) setLoadError(err.message);
      })
      .finally(() => { if (active) setLoading(false); });
    getCharacters();
    return () => { active = false; };
    // Context refresh functions are recreated by App; the selected ID controls loading.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, retry]);

  function characterNameGetter(id) {
    let match = characters.filter((character) => character.id === id);
    if (match.length === 0) {
      return "";
    } else {
      return match[0].name;
    }
  }

  function actionButtons(update) {
    const save = async (update, action) => {
      const response = await apiFetch(`${API}/session/${id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ update, action }),
      });
      setSessionData(await response.json());
      getCharacters();
    };
    const postDelete = (update) => save(update, "DELETE_ITEM");
    return (
      <>
        <Button
          size="small"
          variant="contained"
          disabled={pending}
          onClick={() => run(() => save(update, "UPDATE_ITEM"))}
        >
          Send Update
        </Button>
        <DeleteButton buttonAction={{postDelete}} dialogInfo={update} />
      </>
    );
  }
  let columns = [
    { field: "id", headerName: "ID", width: 50 },
    { field: "raid_team", headerName: "Raid Team", width: 200 },
    {
      field: "character_id",
      headerName: "Character",
      width: 200,
      valueGetter: (value) => characterNameGetter(value.value),
    },
    { field: "item", headerName: "Item", width: 350 },
    { field: "itemId", headerName: "Item ID", width: 75 },
    { field: "dkp", headerName: "DKP", width: 75, editable: true },
    { field: "created_at", headerName: "created_at", width: 200 },
    { field: "updated_at", headerName: "updated_at", width: 200 },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      renderCell: (params) => actionButtons(params.row),
    },
  ];

  if (loading || loadError) return (
    <Box>
      <NavBar pageName="Raid Session" />
      {loading ? <p>Loading raid session...</p> : <>
        <Alert severity="error">{loadError}</Alert>
        <Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>
      </>}
    </Box>
  );
  return (
    <Box sx={{}}>
      <NavBar pageName={`Session: ${session}`} />
      {error && <Alert severity="error">{error}</Alert>}
      <Grid container spacing={2}>
        <Grid item xs={4}>
          <Stack spacing={1}>
            <AddItem setSessionData={setSessionData} />
          </Stack>
        </Grid>
        <Grid zeroMinWidth item xs={4}>
          <Stack spacing={1}>
            <AddCharacter setSessionData={setSessionData} />
          </Stack>
        </Grid>
        <Grid item xs={4}>
          <Stack spacing={1.5}>
            <StopSession />
          </Stack>
        </Grid>
        <Grid item xs={10}>
          <AddonInit />
        </Grid>
        <Grid item xs={2}>
          <Stack spacing={1}>
          <AttendanceDKPUpdate setSessionData={setSessionData} />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <div style={{ height: 800, width: "100%" }}>
            <DataGrid
              rows={sessionData}
              columns={columns}
              initialState={{
                sorting: { sortModel: [{ field: "id", sort: "desc" }] },
              }}
            />
          </div>
        </Grid>
      </Grid>
    </Box>
  );
}
